import {
  Card,
  CardHeader,
  Divider,
  CardContent,
  FormControl,
  InputLabel,
  OutlinedInput,
  CardActions,
  Button,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import React, { useState, useEffect } from "react";
import { useUpdateUserInfo } from "../../../../../api/user/use-update-user";
import { useDeleteUser } from "../../../../../api/user/use-delete-user";
import { useGroups } from "../../../../../api/group/use-groups";
import { useSnackbar } from "../../../../../context/SnackbarContext";
import { useParams, useNavigate } from "react-router-dom";
import { useGetUser } from "../../../../../api/user/use-get-user";
import Loader from "../../../../../components/Loader";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../store";
import { useSetUserPassword } from "../../../../../api/user/use-set-user-password";

function UserEdit() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const [adminNewPassword, setAdminNewPassword] = useState("");
  const { mutate: setUserPassword, isPending: isPasswordPending } =
    useSetUserPassword();
  const { data: selectedUser, isLoading: userLoading } = useGetUser(
    userId || ""
  );
  const updateUserInfoMutation = useUpdateUserInfo();
  const deleteUserMutation = useDeleteUser();
  const { showSnackbar } = useSnackbar();
  const { data: groups, isLoading: groupsLoading } = useGroups();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [username, setUsername] = useState("");
  const [selectedGroup, setSelectedGroup] = useState<string | number>("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  useEffect(() => {
    if (selectedUser) {
      setFirstName(selectedUser.first_name || "");
      setLastName(selectedUser.last_name || "");
      setUsername(selectedUser.username || "");
      setSelectedGroup(selectedUser.user_group_id || "");
    }
  }, [selectedUser]);

  if (userLoading) return <Loader />;

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (firstName.trim().length < 2) {
      showSnackbar("First name must be at least 2 characters", "error");
      return;
    }
    if (lastName.trim().length < 2) {
      showSnackbar("Last name must be at least 2 characters", "error");
      return;
    }
    if (!selectedGroup) {
      showSnackbar("Please select a role", "error");
      return;
    }

    const userData = {
      data: {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        username: username.trim(),
        user_group_id: selectedGroup,
      },
      userId: userId || selectedUser?.id,
    };

    if (userData.userId) {
      updateUserInfoMutation.mutate(userData, {
        onSuccess: () => {
          showSnackbar("User info updated successfully", "success");
        },
        onError: (error: any) => {
          const errorMsg = error?.message || "Failed to update user info";
          showSnackbar(errorMsg, "error");
        },
      });
    } else {
      showSnackbar("User ID is missing", "error");
    }
  };

  const handleOpenDeleteDialog = () => {
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleDeleteConfirm = () => {
    const id = userId || selectedUser?.id;
    if (!id) {
      showSnackbar("User ID is missing", "error");
      return;
    }
    deleteUserMutation.mutate(id, {
      onSuccess: () => {
        showSnackbar("User deleted successfully", "success");
        navigate(`/management/users`);
      },
      onError: (error: any) => {
        const errorMsg = error?.message || "Failed to delete user";
        showSnackbar(errorMsg, "error");
      },
    });
    setOpenDeleteDialog(false);
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader
            subheader="The information can be edited"
            title="Profile"
          />
          <Divider />
          <CardContent>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth required>
                  <InputLabel>First name</InputLabel>
                  <OutlinedInput
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    label="First name"
                    name="firstName"
                  />
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth required>
                  <InputLabel>Last name</InputLabel>
                  <OutlinedInput
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    label="Last name"
                    name="lastName"
                  />
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Email address</InputLabel>
                  <OutlinedInput
                    value={selectedUser?.email ?? ""}
                    label="Email address"
                    name="email"
                    readOnly
                    disabled
                  />
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Username</InputLabel>
                  <OutlinedInput
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    label="Username"
                    name="username"
                  />
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth required>
                  <InputLabel>Role</InputLabel>
                  <Select
                    label="Role"
                    value={selectedGroup}
                    onChange={(e) => setSelectedGroup(e.target.value)}
                    disabled={groupsLoading}
                  >
                    {groups &&
                      groups.map((group: any) => (
                        <MenuItem key={group.id} value={group.id}>
                          {group.title}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 6, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Set new password </InputLabel>
                  <OutlinedInput
                    type="password"
                    value={adminNewPassword}
                    onChange={(e) => setAdminNewPassword(e.target.value)}
                    label="Set new password"
                    name="adminNewPassword"
                    autoComplete="new-password"
                  />
                </FormControl>
                <Button
                  sx={{ mt: 1 }}
                  variant="contained"
                  color="warning"
                  onClick={() => {
                    if (!adminNewPassword || adminNewPassword.length < 8) {
                      showSnackbar(
                        "Password must be at least 8 characters",
                        "error"
                      );
                      return;
                    }
                    setUserPassword(
                      {
                        userId: userId || selectedUser?.id,
                        password: adminNewPassword,
                      },
                      {
                        onSuccess: () => {
                          showSnackbar(
                            "Password updated successfully",
                            "success"
                          );
                          setAdminNewPassword("");
                        },
                        onError: (err) =>
                          showSnackbar(
                            err?.response?.data?.message ||
                              "Failed to update password",
                            "error"
                          ),
                      }
                    );
                  }}
                  disabled={isPasswordPending || !adminNewPassword}
                >
                  Set New Password
                </Button>
              </Grid>
            </Grid>
          </CardContent>
          <Divider />
          <CardActions sx={{ justifyContent: "space-between" }}>
            <Button
              variant="outlined"
              color="error"
              onClick={handleOpenDeleteDialog}
              disabled={user?.id.toString() == userId}
            >
              {deleteUserMutation.isPending ? "Deleting..." : "Delete User"}
            </Button>
            <Button
              variant="contained"
              type="submit"
              disabled={updateUserInfoMutation.isPending}
            >
              {updateUserInfoMutation.isPending ? "Saving..." : "Save details"}
            </Button>
          </CardActions>
        </Card>
      </form>

      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm User Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this user? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default UserEdit;
